import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import html2pdf from "html2pdf.js";
import { getStoredItem } from "../../config/storageUtils";
import api from "../../api/ApiService";
import { documentTypeMap } from "../../config/incentiveClaimFormUtils";
import { incentiveClaimApplicationViewSections } from "../../forms/incentiveClaimApplicationFields";
import {
  installmentsTypes,
  schemeTypes,
} from "../../config/appliedSchemesConfig";
import ApplicationSectionRenderer from "../Admin/ApplicationView/ApplicationSectionRenderer";
import { getKeyByValue } from "../../utils/utils";
import "../../components/common/Css/Pdf.css";
import KeyValueGrid from "../Admin/ApplicationView/KeyValueGrid";
import SectionCard from "../Admin/ApplicationView/SectionCard";

const ApplicationPrint = forwardRef(
  (
    {
      applicationId,
      incentiveClaimId,
      installmentType,
      userId,
      onDone,
      isUserSide = true,
      language = false,
      applicationDetailsSampleData = null,
    },
    ref,
  ) => {
    const [formData, setFormData] = useState(null); // null until API returns
    // const [applicationIdState, setApplicationIdState] =
    //   useState(propApplicationId);
    // const [incentiveClaimIdState, setIncentiveClaimIdState] =
    //   useState(propIncentiveClaimId);
    // const [installmentTypeState, setInstallmentTypeState] =
    //   useState(propInstallmentType);

    // const isReady =
    //   !!applicationIdState && !!incentiveClaimIdState && !!installmentTypeState;

    const isInterest =
      String(incentiveClaimId) === schemeTypes.interestassistance.value;
    const isReimbursement =
      String(incentiveClaimId) ===
      schemeTypes.sreimbursementcapitalincentive.value;

    const isFirstInstallment =
      installmentType === installmentsTypes.first.value;
    const isSecondInstallment =
      installmentType === installmentsTypes.second.value;
    const isThirdInstallment =
      installmentType === installmentsTypes.third.value;

    useEffect(() => {
      if (!formData) return;

      // wait DOM render
      const timer = setTimeout(() => {
        generatePdfInternal();
      }, 400);

      return () => clearTimeout(timer);
    }, [formData]);

    const isReady =
      !!applicationId && !!incentiveClaimId && !!installmentType && !!userId;
    useEffect(() => {
      if (!isReady) return;

      const fetchData = async () => {
        try {
          const [appRes, docRes] = await Promise.all([
            api.get(`/get-caf/${applicationId}?user_id=${userId}`),
            api.post(`/sub-categories/documents`, {
              category_id: 1,
              application_id: applicationId,
              user_id: userId,
            }),
          ]);

          const mainData = appRes.data || {};
          const docData = {};

          docRes?.data?.forEach((doc) => {
            const fieldName = getKeyByValue(documentTypeMap, doc.name_en);
            docData[fieldName] = doc.upload_documents?.[0]
              ? {
                  url: doc.upload_documents[0].path,
                  id: doc.upload_documents[0].id,
                  name: doc.upload_documents[0].filename,
                  label: doc.name_en,
                  docTypeId: doc.id,
                }
              : {
                  url: "",
                  id: "",
                  name: "",
                  label: doc.name_en,
                  docTypeId: doc.id,
                };
          });

          setFormData({ ...mainData, ...docData });
        } catch (err) {
          console.error("Error fetching PDF data:", err);
        }
      };

      fetchData();
    }, [applicationId, incentiveClaimId, installmentType, userId, isReady]);

    useImperativeHandle(ref, () => ({
      generatePdf: generatePdfInternal,
    }));

    const fixOklchColors = (element) => {
      const nodes = element.querySelectorAll("*");

      nodes.forEach((el) => {
        const style = window.getComputedStyle(el);

        if (style.color.includes("oklch")) {
          el.style.color = "rgb(0,0,0)";
        }

        if (style.backgroundColor.includes("oklch")) {
          el.style.backgroundColor = "rgb(255,255,255)";
        }

        if (style.borderColor.includes("oklch")) {
          el.style.borderColor = "rgb(0,0,0)";
        }
      });
    };

    // PDF generation
    const generatePdfInternal = () => {
      if (!formData) {
        console.warn("PDF cannot generate: formData not ready yet");
        return;
      }

      const element = document.getElementById("application-print-wrapper");
      if (!element) return;

      fixOklchColors(element);

      const options = {
        margin: [5, 0, 0, 5],
        filename: "Application_Print.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      html2pdf()
        .set(options)
        .from(element)
        .save()
        .then(() => {
          if (onDone) onDone();
        })
        .catch((err) => {
          console.error("PDF error", err);
          if (onDone) onDone();
        });
    };

    if (!formData) return <div className="hidden">Loading PDF data...</div>;

    const dynamicSections = incentiveClaimApplicationViewSections
      .map((section, i) => {
        let title = section.title;

        if (isInterest && isFirstInstallment)
          title = language
            ? title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण")
            : title.en.replace("Stamp Duty", "Subsidy Details");
        else if (isReimbursement && isSecondInstallment)
          title = language
            ? title.hi.replace("स्टाम्प शुल्क", "कर दस्तावेज़")
            : title.en.replace("Stamp Duty", "Tax Documents");
        else if (isInterest && (isSecondInstallment || isThirdInstallment))
          title = language
            ? title.hi.replace("स्टाम्प शुल्क", "सब्सिडी विवरण")
            : title.en.replace("Stamp Duty", "Subsidy Details");

        return { key: `section-${i}`, label: title, section };
      })
      .filter(
        (tab) =>
          !(
            isReimbursement &&
            tab.label.en.toLowerCase().includes("interest subsidy")
          ),
      );

    return (
      <div className="absolute -left-[9999px] top-0">
        <section
          className="print-pdf text-[10px]"
          id="application-print-wrapper"
        >
          {applicationDetailsSampleData && <div className="bg-white rounded p-3 pdf-section">
            <SectionCard
              title={language ? "आवेदन विवरण : " : "APPLICATION DETAILS :"}
              color="blue"
            >
              <KeyValueGrid data={applicationDetailsSampleData} />
            </SectionCard>
          </div>}
          {dynamicSections.map((item) => (
            <div key={item.key} className="pdf-section">
              <ApplicationSectionRenderer
                section={item.section}
                formData={formData}
                setFormData={setFormData}
                incentiveClaimId={incentiveClaimId}
                installmentType={installmentType}
                forPdf={true}
              />
            </div>
          ))}
        </section>
      </div>
    );
  },
);

export default ApplicationPrint;
